import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";

export const Tabs = TabsPrimitive.Root;

export function TabsList({ className, ...rest }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn("tabs", className)} {...rest} />;
}

/** Radix sets aria-selected on triggers, which `.tab[aria-selected="true"]` styles. */
export function TabsTrigger({ className, ...rest }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return <TabsPrimitive.Trigger className={cn("tab", className)} {...rest} />;
}

export function TabsContent({ className, ...rest }: ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn("rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary", className)} {...rest} />;
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  "aria-label": string;
  className?: string;
  /** Classes of every option button (a phone screen makes them thumb-sized: `h-11 px-4 text-sm`). */
  itemClassName?: string;
}

/** Pill tabs (`.segmented`) on a Radix single-select ToggleGroup; never allows an empty value. */
export function Segmented<T extends string>({ options, value, onValueChange, className, itemClassName, ...rest }: SegmentedProps<T>) {
  const handleChange = (next: string) => {
    const match = options.find((option) => option.value === next);
    if (match) onValueChange(match.value);
  };
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={handleChange}
      className={cn("segmented", className)}
      aria-label={rest["aria-label"]}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          className={cn("data-[state=on]:bg-surface data-[state=on]:text-fg data-[state=on]:shadow-xs", itemClassName)}
        >
          {option.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
